import React, { useState } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useDebouncedCallback } from 'use-debounce'
import { searchShippers } from 'apps/marketplace/utils/api/search'

export interface IShipperResult {
  id: string
  name?: string
}

interface IShipperAutosuggestProps extends IAutosuggestWrapperProps<IShipperResult> {
  confirmed?: boolean
  defaultValue?: IShipperResult
}

const ShipperAutosuggest = ({
  confirmed = true,
  dataTest = 'shipper-autosuggest',
  defaultValue,
  label = 'Shipper',
  onChange: onChangeProp,
  placeholder = 'Enter shipper name',
  value: valueProp,
  ...props
}: IShipperAutosuggestProps) => {
  let convertedDefaultValue
  if (defaultValue) {
    convertedDefaultValue = {
      id: defaultValue.id,
      text: defaultValue.name || '',
      groupIndex: 0,
    }
  }

  const defaultOptions = defaultValue && convertedDefaultValue ? [convertedDefaultValue] : undefined
  const [shipperResults, setShipperResults] = useState<IAutocompleteOption[] | undefined>(
    defaultOptions
  )

  const fetchShippers = async (searchTerm: string) => {
    const results = await searchShippers(searchTerm, confirmed)
    const options = getAutosuggestOptions({
      options: results.namesearch,
      groupIndex: 1,
      mapOption: ({ id, name: text }: IShipperResult) => ({ id, text }),
    })
    setShipperResults(options)
  }

  const changeHandler = async (value?: string) => {
    if (value && value.length > 0) {
      await fetchShippers(value)
      const shipperResult = shipperResults?.find(result => result.text === value)
      if (shipperResult) {
        onChangeProp({
          id: shipperResult.id,
          name: shipperResult.text,
        })
      }
    } else {
      onChangeProp({})
    }
  }

  const [debouncedChangeHandler] = useDebouncedCallback((value: string) => {
    changeHandler(value)
  }, 500)

  return (
    <Autosuggest
      id='shipper'
      dataTest={dataTest}
      value={convertedDefaultValue || valueProp}
      placeholder={placeholder}
      options={shipperResults}
      clearable
      label={label}
      onChange={onChangeProp}
      onInputChange={debouncedChangeHandler}
      large={false}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  )
}

export default ShipperAutosuggest
